import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  ViewChild,
  output,
  linkedSignal,
} from '@angular/core';
import { FileService } from '@shared/services/file.service';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import {
  DialogSize,
  WcgTableData,
  WcgTableRow,
  WcgTimeOnlyPipe,
} from '@wcg/frontend-framework';
import { TableConfirmationDialogComponent } from './table-confirmation-dialog/table-confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { RequestLimiterService } from '@shared/services/request-limiter.service';
import { AuthService } from '@auth/auth.service';
import {
  GRID_VIEW_ACTIONS,
  GridType,
  GridViewFacadeService,
} from '@data-access/grid-views';
import { Store } from '@ngrx/store';
import { WcgTableValues } from '@shared/interfaces/wcg-table-utils.interface';

import {
  DOWNLOAD_MESSAGE_ONE,
  DOWNLOAD_MESSAGE_THREE,
  DOWNLOAD_MESSAGE_TWO,
  EXPORT_MESSAGE,
} from '@data-access/downloads-exports/constants/downloads-exports.constant';
import {
  DownloadsExportFacadeService,
  ExportRequest,
} from '@data-access/downloads-exports';
import { createProcessingData } from '@data-access/downloads-exports/utils';
import { buildExportData } from '@data-access/downloads-exports/utils/build-export-data.util';

@Component({
  selector: 'table-actions',
  styleUrls: ['./table-actions.component.scss'],
  templateUrl: './table-actions.component.html',
  providers: [WcgTimeOnlyPipe],
  standalone: false,
})
export class TableActionsComponent implements OnInit {
  private readonly _injector = inject(Injector);
  private readonly _gridViewFacadeService = inject(GridViewFacadeService);
  private readonly _store = inject(Store);
  private readonly _downloadsExportFacadeService = inject(
    DownloadsExportFacadeService,
  );
  private readonly _dialog = inject(MatDialog);
  private readonly _fileService = inject(FileService);
  private readonly _wcgTableUtils = inject(WcgTableUtilsService);
  private readonly _requestLimiter = inject(RequestLimiterService);
  private readonly _authService = inject(AuthService);

  enableExport = input<boolean>(true);
  enableRevert = input<boolean>(true);
  enableGridView = input<boolean>(true);
  enableDownload = input<boolean>(true);
  enableDelete = input<boolean>(true);
  studyId = input<string>('');
  entityId = input<string>('');
  entityType = input<string>('');
  isOutcome = input<boolean>(false);
  wcgTableValues = input<WcgTableValues>({} as WcgTableValues);
  apiURL = input<string>('');
  readonly selectedRows = input<WcgTableRow[] | string[]>([]);
  readonly fileName = input<string | number>('');
  readonly fileCategory = input('');
  readonly totalDoc = input(0);
  readonly tableData = input<WcgTableData>({} as WcgTableData);
  readonly isTableColumnChanged = input(signal<boolean>(false));
  readonly gridType = input<GridType>('' as GridType);

  readonly revertGridSetting = output<void>();
  readonly resetSelection = output<void>();
  readonly deleteComplete = output<string | string[]>();

  readonly $selectedRows = linkedSignal({
    computation: (selectedRows) => selectedRows,
    source: this.selectedRows,
  });

  @ViewChild('exportMenu') exportMenu!: ElementRef;

  exportMenuOpen = false;

  totalGridViews = this._gridViewFacadeService.$totalGridViews;

  ngOnInit(): void {
    this._store.dispatch(
      GRID_VIEW_ACTIONS.getGridViewsAction({ gridType: this.gridType() }),
    );
  }

  toggleExport(event: MouseEvent) {
    event.stopPropagation();
    this.exportMenuOpen = !this.exportMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  closeDropdownOnClickOutside(_: MouseEvent) {
    this.exportMenuOpen = false;
  }

  handleDownloadDocuments() {
    this.downloadFiles();
  }

  getMessage(type: string): string {
    const selectedCount = this.$selectedRows().length;
    const pluralDocs = `${this.totalDoc()} documents`;
    const $selectedRows = this.$selectedRows() as WcgTableRow[];
    const singularDocTitle =
      $selectedRows[0]?.dataItem?.cellData?.title ||
      $selectedRows[0]?.dataItem?.cellData?.file_name;

    switch (type) {
      case 'download': {
        const downloadMessages = new Map<number, string>([
          [0, DOWNLOAD_MESSAGE_ONE],
          [1, DOWNLOAD_MESSAGE_TWO],
          [2, DOWNLOAD_MESSAGE_THREE],
        ]);

        const messageKey =
          selectedCount === 0 ? 0 : selectedCount === 1 ? 1 : 2;
        return downloadMessages.get(messageKey) || '';
      }

      case 'export':
        return EXPORT_MESSAGE;

      case 'delete':
        if (selectedCount === 0) {
          return `Are you sure you want to delete all ${pluralDocs}? You cannot revert these changes once you delete.`;
        } else if (selectedCount === 1) {
          return `Are you sure you want to delete ${singularDocTitle}? You cannot revert this change once you delete.`;
        }
        return `Are you sure you want to delete the ${selectedCount} documents listed below?`;

      default:
        return 'Are you sure?';
    }
  }

  getBtnTitle(type: string): string {
    const hasSelection = this.$selectedRows().length > 0;

    switch (type) {
      case 'download':
        return hasSelection ? 'Download' : 'Download All';

      case 'delete':
        return hasSelection ? 'Delete' : 'Delete All';

      default:
        return 'Ok';
    }
  }

  getDocuments() {
    return this.$selectedRows().map(
      (row: any) =>
        row.dataItem.cellData.title || row.dataItem.cellData.file_name,
    );
  }

  revertGridSettings() {
    // TODO: The 'emit' function requires a mandatory void argument
    this.revertGridSetting.emit();
  }

  onResetSelection() {
    this.resetSelection.emit();
  }

  downloadFiles() {
    const files = {
      study_id: this.studyId(),
      entity_id: this.entityId(),
      entity_type: this.entityType(),
      ids: (this.$selectedRows() as WcgTableRow[]).map(
        (row: WcgTableRow) => row.dataItem.cellData.id,
      ),
      is_outcome: this.isOutcome(),
    };

    const data = createProcessingData(this.getMessage('download'));

    this._downloadsExportFacadeService.initiateDownload(files, data);
  }

  handleExport(type: string): void {
    const exportData = buildExportData(
      this.wcgTableValues(),
      type,
      this._injector,
    );
    const data = createProcessingData(EXPORT_MESSAGE);

    const exportRequest: ExportRequest = {
      url: this.apiURL(),
      wcgTableValues: exportData,
    };

    this._downloadsExportFacadeService.initiateExport(exportRequest, data);
  }

  handleDeleteDocuments() {
    const dialogRef = this._dialog.open(TableConfirmationDialogComponent, {
      panelClass: 'wcg-confirmation-_dialog',
      data: {
        title: 'Bulk Delete',
        message: this.getMessage('delete'),
        dialogSize: DialogSize.MEDIUM,
        buttonColor: '#BB1128',
        buttonTitle: this.getBtnTitle('delete'),
        documents: this.$selectedRows().length == 1 ? [] : this.getDocuments(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.$selectedRows().length == 0) {
          this.deleteAllDocs();
          return;
        }
        this.deleteDocs();
      }
    });
  }

  deleteAllDocs() {
    this._authService.getToken().then((token) => {
      const headers = {
        Authorization: `Bearer ${token?.accessToken?.accessToken}`,
      };

      const pageSize = this.totalDoc() < 100 ? this.totalDoc() : 100;

      const urls = Array.from(
        { length: Math.ceil(this.totalDoc() / pageSize) },
        (_, i) =>
          `${this.apiURL()}?${this._wcgTableUtils
            .generateTableQueryParams(this.wcgTableValues())
            .set('page', (i + 1).toString())
            .set('page_size', pageSize.toString())}`,
      );

      this._requestLimiter.requestAll(urls, 1, 3, headers).then((results) => {
        const jsonData = results.success.reduce(
          (arr, item) => arr.concat(item.data),
          [],
        );

        this._fileService
          .deleteFilesAsObservable(
            jsonData.map((row: any) => ({
              id: row.id,
              entityId: row.entity_id,
              entityType: row.entity_typ,
            })),
          )
          .subscribe(() => {
            this.deletedDocuments(true);
          });
      });
    });
  }

  deleteDocs() {
    this._fileService
      .deleteFilesAsObservable(
        this.$selectedRows().map((row: any) => ({
          id: row.dataItem.cellData.id,
          entityId: row.dataItem.cellData.entity_id,
          entityType: row.dataItem.cellData.entity_type,
        })),
      )
      .subscribe(() => {
        this.deletedDocuments();
      });
  }

  deletedDocuments(isDeleteAll = false) {
    if (isDeleteAll) {
      this.deleteComplete.emit('all');
      this.$selectedRows.set([]);
    } else {
      const ids: any[] = this.$selectedRows().map(
        (data: any) => data.dataItem.cellData.id,
      );
      this.deleteComplete.emit(ids);
      this.$selectedRows.set([]);
    }
  }

  get isDataAvailable(): boolean {
    return this.totalDoc() > 0;
  }
}
